// modules
import { has, isArray, isEmpty, omit, sortBy } from 'lodash'

// constants
import { MEASUREMET_TYPE_LIST, STATUSES } from 'src/constants'

export const moveItem = (arr = [], fromIndex = 0, toIndex = 0) => {
  const element = arr[fromIndex]
  const updArray = [...arr]

  updArray.splice(fromIndex, 1)
  updArray.splice(toIndex, 0, element)

  return updArray
}

export const getStatuses = (addInactive = false) =>
  Array.from(STATUSES).reduce((result, [key, value]) => {
    if (!addInactive && key !== 'Inactive') {
      result.push(value)
    } else if (addInactive) {
      result.push(value)
    }
    return result
  }, [])

export const findItemByField = ({
  itemList = [],
  field = '',
  value = null,
  childrenField = 'children'
}) => {
  let result = null

  itemList.forEach((item) => {
    if (result) return

    if (item[field] === value) {
      result = { ...item }
    } else if (item[childrenField] && item[childrenField].length > 0) {
      result = findItemByField({
        itemList: item[childrenField],
        field,
        value,
        childrenField
      })
    }
  })

  return result
}

export const makeOneLevelArray = (
  itemList = [],
  childrenField = 'children'
) => {
  let result = []

  itemList.forEach((item) => {
    result.push(omit(item, [childrenField]))

    if (has(item, childrenField) && isArray(item[childrenField])) {
      result = result.concat(
        makeOneLevelArray(item[childrenField], childrenField)
      )
    }
  })

  return result
}

export const mapBillingCodes = (itemList = [], childrenField = 'children') => {
  const mappedCodes = []
  const uuid99445 = itemList?.find((e) => e?.code === '99445')?.uuid
  const uuid99457 = itemList?.find((e) => e?.code === '99457')?.uuid

  sortBy(itemList, ({ type }) => type).forEach((item, key) => {
    const {
      uuid,
      code: billingCode,
      shortDescription,
      description,
      codeFamilyName,
      type,
      isDefault
    } = item
    const rpmCodes = ['99454', '99457']
    const rpmCodeFamily =
      billingCode === '99454' ? '99445/99454' : codeFamilyName
    const includesRpm = rpmCodes.includes(billingCode)
    const isTcmFamilyItem = billingCode === 'TCM'

    const title = includesRpm
      ? `${rpmCodeFamily} ${shortDescription || description || ''}`
      : isTcmFamilyItem && codeFamilyName
      ? `${codeFamilyName} - ${shortDescription || description || ''}`
      : `${billingCode} - ${shortDescription || description}`

    let pushedItem = {
      key,
      title,
      value: includesRpm
        ? `${billingCode === '99454' ? uuid99445 : uuid99457},${uuid}`
        : uuid,
      billingCode,
      type,
      isDefault
    }

    if (!isEmpty(item[childrenField])) {
      const itemArray = [
        { ...item, shortDescription: null, [childrenField]: [] },
        ...item[childrenField]
      ]
      const descriptionArray = []
      const codeArray = []
      const uuidArray = []

      itemArray.forEach(({ uuid, code, description, shortDescription }) => {
        uuidArray.push(uuid)
        codeArray.push(
          billingCode === '99454'
            ? '99445/99454'
            : billingCode === '99457' && !codeArray?.includes('99470/99457')
            ? '99470/99457'
            : code
        )

        descriptionArray.push(shortDescription || description)
      })

      pushedItem = {
        key,
        title:
          codeFamilyName === 'TCM'
            ? codeFamilyName
            : `${codeArray.join('/')} - ${descriptionArray.join(', ')}`,
        value: includesRpm
          ? `${billingCode === '99454' ? uuid99445 : uuid99457},${uuid}`
          : uuidArray.join(','),
        type,
        isDefault
      }
    }

    if (!['99470', '99445'].includes(billingCode)) {
      mappedCodes.push(pushedItem)
    }
  })

  return mappedCodes
}

export const mapBillingCodesByCode = (codes = [], withNewRpmCodes = true) => {
  const mappedCodes = []

  sortBy(codes, ({ type }) => type).forEach(
    ({
      code: billingCode,
      description,
      children,
      isDefault,
      codeFamilyName
    }) => {
      const rpmCodes = ['99454', '99457']
      const rpmCodeFamily =
        billingCode === '99454' ? '99445/99454' : codeFamilyName
      const includesRpm = rpmCodes.includes(billingCode) && withNewRpmCodes
      const isTcmFamilyItem = billingCode === 'TCM'
      const childParts = children?.map(
        (child) => `${child.code} - ${child.description}`
      )

      const childrenString = childParts?.join(' / ') || ''

      const title = includesRpm
        ? `${rpmCodeFamily} ${description}${
            childrenString ? ` / ${childrenString}` : ''
          }`
        : isTcmFamilyItem && codeFamilyName
        ? `${codeFamilyName} - ${description || ''}`
        : `${billingCode} - ${description}${
            childrenString ? ` / ${childrenString}` : ''
          }`

      if (!isEmpty(children)) {
        const code = {
          key: billingCode === '99454' ? '99454,99445' : billingCode,
          title: codeFamilyName === 'TCM' ? codeFamilyName : title,
          value: includesRpm
            ? billingCode === '99454'
              ? '99454,99445'
              : '99470,99457'
            : [billingCode, ...children.map(({ code }) => code)].join(','),
          isDefault
        }

        mappedCodes.push(code)
      } else {
        const code = {
          key: billingCode,
          title: includesRpm || isTcmFamilyItem ? title : `${billingCode} - ${description}`,
          value: includesRpm
            ? billingCode === '99454'
              ? '99454,99445'
              : '99470,99457'
            : billingCode,
          isDefault
        }

        if (
          withNewRpmCodes ? !['99470', '99445'].includes(billingCode) : true
        ) {
          mappedCodes.push(code)
        }
      }
    }
  )

  return mappedCodes
}

export const checkIsEmptyArray = (arr = []) => {
  if (!isArray(arr)) return true

  const isEmpty = !arr.filter(
    (value = null) => typeof value === 'number' || value
  ).length

  return isEmpty
}

export const getImageSrcByTitleOrType = (searchValue = 0) => {
  switch (searchValue) {
    case MEASUREMET_TYPE_LIST.get(1)?.title:
    case MEASUREMET_TYPE_LIST.get(1)?.type:
      return require('src/assets/images/measurements/weight.svg')

    case MEASUREMET_TYPE_LIST.get(2)?.title:
    case MEASUREMET_TYPE_LIST.get(2)?.type:
      return require('src/assets/images/measurements/glucose_level.svg')

    case MEASUREMET_TYPE_LIST.get(3)?.title:
    case MEASUREMET_TYPE_LIST.get(3)?.type:
      return require('src/assets/images/measurements/blood_pressure.svg')

    case MEASUREMET_TYPE_LIST.get(4)?.title:
    case MEASUREMET_TYPE_LIST.get(4)?.type:
      return require('src/assets/images/measurements/mood.svg')

    case MEASUREMET_TYPE_LIST.get(5)?.title:
    case MEASUREMET_TYPE_LIST.get(5)?.type:
      return require('src/assets/images/measurements/pain.svg')

    case MEASUREMET_TYPE_LIST.get(6)?.title:
    case MEASUREMET_TYPE_LIST.get(6)?.type:
      return require('src/assets/images/measurements/temperature.svg')

    case MEASUREMET_TYPE_LIST.get(7)?.backendTitle:
    case MEASUREMET_TYPE_LIST.get(7)?.title:
    case MEASUREMET_TYPE_LIST.get(7)?.type:
      return require('src/assets/images/measurements/spo2.svg')

    case MEASUREMET_TYPE_LIST.get(8)?.backendTitle:
    case MEASUREMET_TYPE_LIST.get(8)?.title:
    case MEASUREMET_TYPE_LIST.get(8)?.type:
      return require('src/assets/images/measurements/pulse_rate.svg')

    case MEASUREMET_TYPE_LIST.get(9)?.backendTitle:
    case MEASUREMET_TYPE_LIST.get(9)?.title:
    case MEASUREMET_TYPE_LIST.get(9)?.type:
      return require('src/assets/images/measurements/respiratory_rate.svg')

    default:
      return ''
  }
}
